import React, { useMemo } from "react";

import { Settings } from "settings";

export type SocketStatsType = "pubsub" | "sharedb";

function median(arr: number[]): number | undefined {
  if (!arr.length) return undefined;
  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
}

function createSocketTypeArrays(): Record<SocketStatsType, number[]> {
  return { pubsub: [], sharedb: [] };
}

export class SocketStatsCollector {
  // First dimension is socket type ("pubsub" | "sharedb")
  private pingRtt: Record<SocketStatsType, number[]> = createSocketTypeArrays();

  private rollingPingRTT: Record<SocketStatsType, number[]> = createSocketTypeArrays();

  public resetStats(socketType?: SocketStatsType) {
    if (socketType) {
      this.pingRtt[socketType] = [];
      this.rollingPingRTT[socketType] = [];
    } else {
      this.pingRtt = createSocketTypeArrays();
      this.rollingPingRTT = createSocketTypeArrays();
    }
  }

  public addPingRecord(socketType: SocketStatsType, rtt: number) {
    this.pingRtt[socketType].push(rtt);
    this.rollingPingRTT[socketType].push(rtt);
    const rolling = this.rollingPingRTT[socketType];
    if (rolling.length > Settings.PUBSUB.ROLLING_PING_RTT_LENGTH) {
      rolling.shift();
    }
  }

  private calculateStats(socketType: SocketStatsType) {
    const pingRtt = this.pingRtt[socketType];
    const rollingPingRTT = this.rollingPingRTT[socketType];

    if (pingRtt.length === 0) {
      return null;
    }

    const rollingMax = Math.max(...rollingPingRTT);
    const rollingMedian = median(rollingPingRTT);

    const isHigh =
      rollingMax >= Settings.PUBSUB.POOR_CONNECTION_EXTREME_PING_THRESHOLD ||
      (rollingMax >= Settings.PUBSUB.POOR_CONNECTION_MAX_PING_THRESHOLD &&
        rollingMedian &&
        rollingMedian >= Settings.PUBSUB.POOR_CONNECTION_MEDIAN_PING_THRESHOLD);

    const isLow =
      rollingMax < Settings.PUBSUB.GOOD_CONNECTION_MAX_PING_THRESHOLD &&
      rollingMedian &&
      rollingMedian < Settings.PUBSUB.GOOD_CONNECTION_MEDIAN_PING_THRESHOLD;

    return {
      isHigh,
      isLow,
      max: Math.max(...pingRtt),
      median: median(pingRtt),
      min: Math.min(...pingRtt),
    };
  }

  public getStats(socketType: SocketStatsType) {
    const stats = this.calculateStats(socketType);
    if (!stats) {
      return null;
    }
    return { pingRtt: stats };
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export const SocketStatsCollectorContext = React.createContext<SocketStatsCollector | null>(null);

export const SocketStatsCollectorProvider = ({ children }: { children: React.ReactNode }) => {
  const contextValue = useMemo(() => new SocketStatsCollector(), []);
  return (
    <SocketStatsCollectorContext.Provider value={contextValue}>
      {children}
    </SocketStatsCollectorContext.Provider>
  );
};
